import { AvailablePlan } from '@dtx-company/inter-app/src/types/AvailablePlan'
import { LockModalGraphic } from '@app/common/src/constants/lockModal'
import { PageContainer } from '../../layout'
import { PlanPurchaseExperience } from '@app/common/src/components/PlanPurchaseExperience/PlanPurchaseExperience'
import { PlanPurchaseExperienceMode } from '@dtx-company/true-common/src/types/Plan'
import {
  PlanPurchaseExperienceProps,
  SuccessfulPlanPurchaseCheckout
} from '@app/common/src/components/PlanPurchaseExperience/types'
import { PlanType } from '@dtx-company/true-common/src/types/planType'
import { PromoAsset } from '@app/common/src/components/LockModal/PromoAsset/PromoAsset'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { UrlObject } from 'url'
import { createBuyPlanPageUrlObject } from '../BuyPlanPage/routing/createBuyPlanPageUrlObject'
import { createPlansPageUrl } from '../Plans/createPlansPageUrl'
import { getPlanTypeDirection } from '@app/common/src/utils/getPlanTypeDirection'
import { getPlanTypeName } from '@app/common/src/utils/getPlanTypeName'
import { isOrgPlan } from '@app/common/src/utils/isOrgPlan'
import { isPilotPlanType } from '@app/common/src/utils/isPilotPlanType'
import {
  sendNotification,
  sendSuccessNotification
} from '@dtx-company/inter-app/src/utils/notifications'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useBillingPlanType } from '@app/common/src/hooks/useBillingPlanType/useBillingPlanType'
import { useBuyPlanPageForPlanType } from '../BuyPlanPage/useBuyPlanPageForPlanType'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useCurrentAvailablePlansSWR } from '@app/common/src/hooks/useCurrentAvailablePlansSWR'
import { useFlowRouter } from '@dtx-company/inter-app/src/hooks/useFlowRouter'
import { usePlanPurchaseExperienceSubscriptionInfo } from '@app/common/src/components/PlanPurchaseExperience/hooks/usePlanPurchaseExperienceSubscriptionInfo'
import { useSuccessfulCheckoutMessage } from '../../../hooks/useSuccessfulCheckoutMessage'
import { useUpgradePageQuery } from './useUpgradePageQuery'

function getRedirectUrl(
  planType?: PlanType,
  mode?: PlanPurchaseExperienceMode,
  isSwitchingBillingCycle?: boolean
): UrlObject {
  if (isSwitchingBillingCycle && !mode) {
    return { pathname: Routes.ACCOUNT }
  }
  if (mode !== PlanPurchaseExperienceMode.CHANGE_PAYMENT && planType && isOrgPlan(planType)) {
    return { pathname: Routes.MY_ORG_INVITATIONS }
  }
  return { pathname: Routes.CREATE_FLOWCODE }
}

export function UpgradePage(): JSX.Element {
  const authState = useAuthState()
  const billingPlanType = useBillingPlanType()

  const availablePlansSWR = useCurrentAvailablePlansSWR(
    {
      include: {
        plan: true
      }
    },
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateOnMount: true
    }
  )

  const flowRouter = useFlowRouter()
  const query = useUpgradePageQuery()
  const mode = query.mode
  const planTypeFromQuery = query.planType
  const [planType, setPlanType] = useState<PlanType | undefined>(undefined)
  const [validated, setValidated] = useState<boolean>()
  const [redirected, setRedirected] = useState(false)
  const planTypeName = getPlanTypeName(planType || PlanType.PRO)

  const successfulCheckoutMessage = useSuccessfulCheckoutMessage({
    currentPlanType: billingPlanType || PlanType.PRO,
    newPlanType: planType || PlanType.PRO
  })

  const redirectToPlans = useCallback(() => {
    sendNotification({
      message: 'That action is not available, please check our other plan options.'
    })
    setRedirected(true)
    flowRouter.replace(createPlansPageUrl())
  }, [flowRouter])

  const redirectToBuyPage = useCallback(() => {
    setRedirected(true)
    flowRouter.replace(
      createBuyPlanPageUrlObject({
        query: {
          planType: planType || PlanType.PRO,
          from: query.from,
          promoCode: query.promoCode
        }
      })
    )
  }, [flowRouter, planType, query.from, query.promoCode])

  const buyPagePrismicDocId = useBuyPlanPageForPlanType(planType)
  const { currentSubscriptionInfoData } = usePlanPurchaseExperienceSubscriptionInfo()

  // prevent loop of redirecting between upgrade and buy page if user is switching billing cycles
  // also prevents redirect when in change payment or addons mode
  const currentPlan = currentSubscriptionInfoData?.plan
  const isSwitchingBillingCycle = currentPlan && planType === currentPlan.type
  const shouldRedirectToBuyPage =
    buyPagePrismicDocId !== undefined && mode === undefined && !isSwitchingBillingCycle

  // Set the initial plan type
  useEffect(() => {
    if (redirected) return
    if (planType) return
    if (!authState.isAuthChecked) return
    if (authState.isAuthenticated && !availablePlansSWR.data) return
    if (planTypeFromQuery) {
      setPlanType(planTypeFromQuery)
    } else if (mode === PlanPurchaseExperienceMode.CHANGE_PAYMENT && billingPlanType) {
      setPlanType(billingPlanType)
    } else if (mode === PlanPurchaseExperienceMode.PURCHASE_ADDONS && billingPlanType) {
      setPlanType(billingPlanType)
    } else if (availablePlansSWR.data) {
      const lowestAvailablePlan = availablePlansSWR.data.reduce<AvailablePlan | undefined>(
        (lowest, availablePlan) => {
          // Has Plan
          const plan = availablePlan.plan
          if (!plan) return lowest
          // Is an upgrade
          const planChangeType = availablePlan.planChangeType
          if (!['NEW', 'UPGRADE'].includes(planChangeType)) return lowest
          // Is an allowed/visible upgrade from /plans page
          const planType = plan.type
          if (![PlanType.PRO, PlanType.PRO_PLUS_ORG].includes(planType)) return lowest
          // Is not the current plan type (if on monthly, yearly is an "upgrade")
          if (planType === billingPlanType) return lowest
          // If no lowest yet, this plan is now valid to be it
          if (!lowest) return availablePlan
          // Otherwise it must be lower than the lowest
          const planLevel = plan.planLevel || 0
          if (planLevel < (lowest.plan?.planLevel || 0)) return availablePlan
          // Keep the lowest so far one
          return lowest
        },
        undefined
      )
      if (lowestAvailablePlan) {
        setPlanType(lowestAvailablePlan.plan?.type)
      } else {
        redirectToPlans()
      }
    } else {
      setPlanType(PlanType.PRO)
    }
  }, [
    redirected,
    planType,
    availablePlansSWR.data,
    planTypeFromQuery,
    mode,
    redirectToPlans,
    billingPlanType,
    authState.isAuthChecked,
    authState.isAuthenticated
  ])

  // Redirect if plan type is not valid
  useEffect(() => {
    if (redirected) return
    if (!planType) return
    if (!authState.isAuthChecked) return

    // wait for current sub data to load if user is logged in
    // if user has no sub (basic plan), currentSubscriptionInfoData is null, so it will move past this check
    if (authState.isAuthenticated && currentSubscriptionInfoData === undefined) return
    if (shouldRedirectToBuyPage) {
      redirectToBuyPage()
      return
    }
    if (!authState.isAuthenticated) {
      // Allow to show the component so the login form can display
      // re-validate once logged in
      setValidated(true)
      return
    }
    if (!availablePlansSWR.data) return
    const oneValidPlan = availablePlansSWR.data.find(availablePlan => {
      return availablePlan.plan?.type === planType
    })
    if (oneValidPlan) {
      setValidated(true)
    } else {
      redirectToPlans()
    }
  }, [
    redirected,
    planType,
    shouldRedirectToBuyPage,
    authState.isAuthChecked,
    authState.isAuthenticated,
    availablePlansSWR.data,
    redirectToBuyPage,
    redirectToPlans,
    currentSubscriptionInfoData
  ])

  async function redirectAfterPlanPurchaseExperience(
    data?: SuccessfulPlanPurchaseCheckout
  ): Promise<void> {
    const redirectUrl = getRedirectUrl(planType, mode, data?.isSwitchingBillingCycle)
    await flowRouter.replace(redirectUrl)
  }

  async function handleSuccessfulCheckout(data: SuccessfulPlanPurchaseCheckout): Promise<void> {
    const { oldPlan, newPlan } = data
    const oldPlanType = oldPlan?.type || PlanType.FREE
    const newPlanType = newPlan?.type || PlanType.PRO

    // For upgrades we open a confetti modal to show congrats before this function is called
    // For other cases, we show a simpler snackbar to thank them
    const direction = getPlanTypeDirection(oldPlanType, newPlanType)
    if (direction <= 0 && !isPilotPlanType(oldPlanType)) {
      sendSuccessNotification(successfulCheckoutMessage)
    }
    await redirectAfterPlanPurchaseExperience(data)
  }

  async function handleClaimFreeMonth(): Promise<void> {
    sendSuccessNotification(`We hope you enjoy your free month of ${planTypeName}`)
    await redirectAfterPlanPurchaseExperience()
  }

  // Reproducing some stuff from lock modals in order to have the /upgrade page have
  // reuse some custom work that was done to enhance the right side graphic as
  // a quickfix for a p1 issue losing pro subscriptions
  const graphic = useMemo<LockModalGraphic | undefined>(() => {
    if (mode) return
    if (planType === PlanType.PRO && billingPlanType !== PlanType.PRO) {
      return {
        header: '',
        src: '/graphics/lockModal/fc-pro-code-limit3-2x.png',
        height: 663,
        width: 305,
        bg: '#242424',
        hideCompanies: true
      }
    }
  }, [mode, planType, billingPlanType])

  const promoProps = useMemo<Partial<PlanPurchaseExperienceProps> | undefined>(() => {
    if (!graphic) return
    return {
      promoAsset: <PromoAsset graphic={graphic} />,
      promoBackgroundImageUrl: graphic.bg,
      promoHeading: graphic.header,
      promoHideCompanies: graphic.hideCompanies
    }
  }, [graphic])

  return (
    <>
      <PageContainer page={Routes.UPGRADE} disableSideNav showOverflow>
        {validated && (
          <PlanPurchaseExperience
            planType={planType}
            onSuccessfulCheckout={handleSuccessfulCheckout}
            onClaimFreeMonth={handleClaimFreeMonth}
            stickyTop={56}
            stickyBottom={0}
            mode={mode}
            {...promoProps}
          />
        )}
      </PageContainer>
    </>
  )
}
